<template>
  <DSFormField
    :id="id"
    #default="{ describedBy }"
    :name="props.name"
    :label="props.label ?? undefined"
    :tooltip="props.tooltip"
    :inline-edit="props.inlineEdit"
    :hidden-label="props.hiddenLabel"
    :required="props.required"
    :disabled="props.disabled || props.readonly"
    :helper-text="props.helperText"
    :error-messages="errorMessages"
  >
    <div
      :class="rootClasses"
      data-cy="ds-url-field"
    >
      <DSBaseInput
        :id="id"
        ref="urlInputRef"
        v-model="modelValue"
        v-ds-tooltip="urlTooltip"
        :type="DS_BASE_INPUT_TYPE.URL"
        :name="props.name"
        :data-cy-suffix="props.name"
        :placeholder="props.placeholder"
        :inline-edit="props.inlineEdit"
        :disabled="props.disabled"
        :readonly="props.readonly"
        :required="props.required"
        :autocomplete="props.autocomplete"
        :maxlength="props.maxlength"
        :minlength="props.minlength"
        :clearable="isClearable"
        :has-error="hasErrors"
        :aria-label="props.ariaLabel"
        :aria-invalid="hasErrors ? 'true' : undefined"
        :aria-describedby="describedBy"
        @update:model-value="onInput"
        @clear="onClear"
        @focus="onFocus"
        @blur="onBlur"
      />

      <DSCopyButton
        v-if="props.showCopyAction && copyValue"
        :value="copyValue"
        :disabled="props.disabled"
        data-cy="ds-url-field__copy-button"
        variant="ghost"
        size="sm"
      />
      <DSButton
        v-if="props.showOpenAction && openHref"
        v-ds-tooltip="i18n.buttonOpen"
        :href="openHref"
        :disabled="props.disabled"
        :aria-label="i18n.buttonOpen"
        target="_blank"
        rel="noopener noreferrer"
        prefix-icon="open-window"
        variant="ghost"
        size="sm"
        data-cy="ds-url-field__open-button"
      />
    </div>
  </DSFormField>
</template>

<script setup lang="ts">
import { ref, toRef } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import DSCopyButton from "@/src/design_system/button_and_controls/copy_button/components/DSCopyButton.vue";
import DSBaseInput from "@/src/design_system/form_elements/base_input/components/DSBaseInput.vue";
import { DS_BASE_INPUT_TYPE } from "@/src/design_system/form_elements/base_input/types/inputType";
import DSFormField from "@/src/design_system/form_elements/form_field/components/DSFormField.vue";
import { useDSFormFieldId } from "@/src/design_system/form_elements/shared/composables/useDSFormFieldId";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { useDSUrlField } from "@/src/design_system/form_elements/url_field/composables/useDSUrlField";
import { useDSUrlFieldTooltip } from "@/src/design_system/form_elements/url_field/composables/useDSUrlFieldTooltip";
import type {
  DSUrlFieldEmits,
  DSUrlFieldModelValue,
  DSUrlFieldProps,
} from "@/src/design_system/form_elements/url_field/types";
import { vDsTooltip } from "@/src/design_system/primitives/tooltip/directives/vDsTooltip";

const props = withDefaults(defineProps<DSUrlFieldProps>(), {
  hiddenLabel: false,
  tooltip: undefined,
  inlineEdit: false,
  disabled: false,
  readonly: false,
  required: false,
  clearable: true,
  showCopyAction: true,
  showOpenAction: true,
  errorMessages: undefined,
  rules: undefined,
});

const modelValue = defineModel<DSUrlFieldModelValue>({ default: "" });
const i18n = window.I18n.designSystem.urlField;
const emit = defineEmits<DSUrlFieldEmits>();

const urlInputRef = ref<InstanceType<typeof DSBaseInput>>();
const { focus, blur } = useDSInput(urlInputRef);

const { copyValue, openHref, errorMessages, hasErrors, isClearable, onClear, onInput, rootClasses } = useDSUrlField({
  props,
  modelValue,
});

const { id } = useDSFormFieldId({ props });

const { tooltip: urlTooltip } = useDSUrlFieldTooltip({
  inputRef: urlInputRef,
  value: modelValue,
  disabled: toRef(props, "disabled"),
});

const onFocus = (event: FocusEvent) => {
  emit("focus", event);
};

const onBlur = (event: FocusEvent) => {
  emit("blur", event);
};

defineExpose({
  focus,
  blur,
});
</script>
